import React, { useEffect, useRef } from 'react';

import { useIntl } from 'react-intl';

import TaskIcon from '@atlaskit/icon/core/task';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import SharedSalesforceDynamicMultiSelect from '../../../../../common/ui/filters/product-filters/shared-salesforce-dynamic-multi-select';
import type { FilterProps } from '../../../../../common/ui/filters/product-filters/types';
import { useSelectFilter } from '../../../../../controllers/filters/base-select-filter';
import { useIsConfigLoaded } from '../../../../../controllers/store/bootstrap';
import { filter as taskStatusFilter } from '../../../../../controllers/store/search/filters/task-status';
import { i18n } from './messages';

const TaskStatusFilter = (props: FilterProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const [filterData, { loadInitialOptions, setOption, clearOptions }] = useSelectFilter({
		filterId: taskStatusFilter.id,
	});
	const shouldEagerLoadOptions = fg('rovo-fps-3p-ux-salesforce-task-status-filter');
	const isConfigLoaded = useIsConfigLoaded();

	// Hold the "did mount" flip until isConfigLoaded is true so the bootstrap-config race
	// cannot leave the filter permanently empty.
	const hasLoadedInitialOptions = useRef(false);

	useEffect(() => {
		if (!shouldEagerLoadOptions || hasLoadedInitialOptions.current) {
			return;
		}
		if (!isConfigLoaded) {
			return;
		}
		hasLoadedInitialOptions.current = true;
		loadInitialOptions();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [shouldEagerLoadOptions, isConfigLoaded]);

	// Hide the filter entirely when the feature gate is off — matching the
	// SalesforceStageFilter pattern. Without this guard the filter would render
	// (empty) because its store `products` list always contains Salesforce.
	if (!filterData || !shouldEagerLoadOptions) {
		return null;
	}

	const { isLoading, suggestedOptions, selectedOptions } = filterData;

	return (
		<SharedSalesforceDynamicMultiSelect
			{...props}
			filterId={taskStatusFilter.id}
			label={formatMessage(i18n.taskStatusFilterLabel)}
			noOptionsMessage={formatMessage(i18n.taskStatusFilterNoOptions)}
			iconBefore={<TaskIcon label="" />}
			isLoading={isLoading}
			selectedOptions={selectedOptions}
			suggestedOptions={suggestedOptions}
			loadInitialOptions={loadInitialOptions}
			setOption={(option) => {
				return setOption(option);
			}}
			clearOptions={() => {
				return clearOptions();
			}}
		/>
	);
};

export default TaskStatusFilter;
